import { Collapse } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";

import { NavItemType } from "types";
import { MENU_LIST_ITEM_VARIANTS } from "../const";
import { SidebarList, SidebarRailGroup } from "../MainSidebar.components";
import MainSidebarMenuListItem from "./MainSidebarMenuListItem";

export interface Props {
  item: NavItemType;
  selectedItem: string;
  isDrawOpen: boolean;
  loading?: boolean;
}

export const MainSidebarMenuTree = ({
  isDrawOpen,
  item,
  loading = false,
  selectedItem
}: Props) => {
  // A top-level group with no children renders nothing; nested children are
  // handled per-child below (only rendered when the child actually has them).
  if (!item.children || item.children.length === 0) return null;

  const renderMenu = (menu: NavItemType[], nested = false) =>
    menu.map((child) => {
      const childItems = child.children ?? [];
      const expandable = childItems.length > 0;
      return (
        <TreeNode
          key={child.id}
          child={child}
          expandable={expandable}
          isDrawOpen={isDrawOpen}
          loading={loading}
          nested={nested}
          renderMenu={renderMenu}
          selectedItem={selectedItem}
        />
      );
    });

  // item.children is guaranteed non-empty by the guard above.
  return (
    // Each group is its own navigation landmark, so each needs its own
    // name — assistive tech (and the a11y check) lists landmarks by name.
    <nav
      aria-label={typeof item.title === "string" ? item.title : item.id}
      data-test-id={`sidebar-menu-tree-${item.id}`}
    >
      <SidebarList open={isDrawOpen}>{renderMenu(item.children)}</SidebarList>
    </nav>
  );
};

interface TreeNodeProps {
  child: NavItemType;
  expandable: boolean;
  isDrawOpen: boolean;
  loading: boolean;
  nested: boolean;
  renderMenu: (menu: NavItemType[], nested?: boolean) => ReactNode;
  selectedItem: string;
}

/**
 * A single tree row. Split out from the render loop so an expandable row can
 * own its open/closed state via a hook. Follows the active route: the group
 * opens when one of its children becomes the selected item and closes again
 * when the selection leaves the group. Between route changes the state is the
 * user's, so a manual toggle sticks.
 *
 * Expanded, an open group's children simply indent beneath it. Collapsed, the
 * parent tile and its children sit together in a tinted block so the grouping
 * stays legible without the labels — and a CLOSED group whose child is the
 * current page lights its parent tile, since the child it would light is not
 * on screen.
 */
const TreeNode = ({
  child,
  expandable,
  isDrawOpen,
  loading,
  nested,
  renderMenu,
  selectedItem
}: TreeNodeProps) => {
  const childItems = child.children ?? [];
  const hasSelectedChild =
    expandable && childItems.some((c) => c.id === selectedItem);
  const [open, setOpen] = useState(hasSelectedChild);

  useEffect(() => {
    setOpen(hasSelectedChild);
  }, [hasSelectedChild]);

  const isGroupOpen = expandable && open;
  const isSelf = selectedItem === child.id;
  const active = isSelf || (!isDrawOpen && !isGroupOpen && hasSelectedChild);

  const row = (
    <MainSidebarMenuListItem
      active={active}
      menuItem={child}
      variant={child.type as MENU_LIST_ITEM_VARIANTS}
      isDrawOpen={isDrawOpen}
      loading={loading || child.skeleton}
      expandable={expandable}
      expanded={open}
      nested={nested}
      onToggle={() => setOpen((prev) => !prev)}
    />
  );

  if (!expandable) return <li>{row}</li>;

  if (!isDrawOpen) {
    return (
      <li>
        {isGroupOpen ? (
          <SidebarRailGroup>
            {row}
            <SidebarList open={false}>
              {renderMenu(childItems, true)}
            </SidebarList>
          </SidebarRailGroup>
        ) : (
          row
        )}
      </li>
    );
  }

  return (
    <li>
      {row}
      <Collapse in={open} unmountOnExit>
        <SidebarList open style={{ paddingTop: 2 }}>
          {renderMenu(childItems, true)}
        </SidebarList>
      </Collapse>
    </li>
  );
};
